import { FC } from "react";
import styled, { css } from "styled-components";

import Text from "@/components/Text";
import Button from "@/components/Button";
import { ReactComponent as PlusIcon } from "@/assets/icons/plus.svg";
import { ReactComponent as ClosedEyeIcon } from "@/assets/icons/closed-eye.svg";

export const Box = styled.div`
  box-shadow: 0px 0px 2px rgba(0, 0, 0, 0.08);
  border-radius: 4px;
  background-color: ${({ theme }) => theme.colors.white};
  margin-bottom: 40px;
`;

const BoxHeading = styled.div`
  padding: 16px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const BoxHeadingButtons = styled.div`
  display: flex;
  gap: 16px;
`;

const BoxAddButton = styled(Button)`
  font-size: 14px;
  line-height: 22px;
  padding: 0 16px;
  height: 38px;
  font-weight: 600;
  border-color: ${({ theme }) => theme.colors.lightGray};
  svg {
    margin-right: 8px;
  }
`;

const ToggleButton = styled.button<{ $isContentVisible?: boolean }>`
  background: ${({ theme }) => theme.colors.grayscaleLight};
  border-radius: 4px;
  height: 38px;
  width: 38px;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1px solid transparent;
  transition: all ease-out 0.2s;
  &:hover {
    border-color: rgba(34, 34, 34, 0.25);
  }
  ${({ $isContentVisible }) =>
    $isContentVisible &&
    css`
      background-color: ${({ theme }) => theme.colors.white};
      border-color: ${({ theme }) => theme.colors.lightGray};
    `}
`;

const Count = styled(Text)`
  width: 24px;
  height: 18px;
  background: ${({ theme }) => theme.colors.lightGray};
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 8px;
`;

const BoxHeader = styled.div`
  display: flex;
  align-items: center;
`;

interface Props {
  setShowModal?: React.Dispatch<React.SetStateAction<boolean>>;
  count?: number;
  showContent: boolean;
  setShowContent: React.Dispatch<React.SetStateAction<boolean>>;
  title: string;
  AddbuttonText?: string;
  hasSubmitButton?: boolean;
  isSubmitting?: boolean;
  isDirty?: boolean;
}

const ToggableCardHeading: FC<Props> = ({
  setShowModal,
  count,
  showContent,
  setShowContent,
  title,
  AddbuttonText,
  hasSubmitButton,
  isSubmitting,
  isDirty,
}) => {
  return (
    <BoxHeading>
      <BoxHeader>
        <Text $type="caption">{title}</Text>
        {!!count && <Count $type="extraSmall">{count}</Count>}
      </BoxHeader>
      <BoxHeadingButtons>
        {!!hasSubmitButton && (
          <Button
            $type="colorized"
            disabled={isSubmitting || !isDirty}
            loading={isSubmitting}
          >
            Save
          </Button>
        )}
        {!!AddbuttonText && !!setShowModal && (
          <BoxAddButton
            type="button"
            onClick={() => setShowModal(true)}
            color="secondary"
          >
            <PlusIcon />
            {AddbuttonText}
          </BoxAddButton>
        )}
        <ToggleButton
          type="button"
          $isContentVisible={showContent}
          onClick={() => setShowContent((prev) => !prev)}
        >
          {showContent ? (
            <svg
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M9 3.93695C3.375 3.93695 1.125 9 1.125 9C1.125 9 3.375 14.062 9 14.062C14.625 14.062 16.875 9 16.875 9C16.875 9 14.625 3.93695 9 3.93695Z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M9 11.8126C10.5533 11.8126 11.8125 10.5534 11.8125 9.00006C11.8125 7.44676 10.5533 6.18756 9 6.18756C7.4467 6.18756 6.1875 7.44676 6.1875 9.00006C6.1875 10.5534 7.4467 11.8126 9 11.8126Z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          ) : (
            <ClosedEyeIcon />
          )}
        </ToggleButton>
      </BoxHeadingButtons>
    </BoxHeading>
  );
};

export default ToggableCardHeading;
